<template>
    <div ref="wrapperRef" class="sp-wrapper">
        <slot 
            name="trigger" 
            :toggle="toggle" 
            :isOpen="isOpen" 
            :activeFilters="activeFilters">            
            <button type="button" class="btn btn-sm sp-trigger" :class="{ active: isOpen }" @click.stop="toggle">
                <i class="fas fa-search"></i>
                {{ title ?? 'Filtrar' }}
                <span v-if="activeFilters > 0" class="sp-badge">{{ activeFilters }}</span>
            </button>
        </slot>
        <Teleport to="body">
            <Transition name="sp-fade">
                <div v-if="isOpen" 
                    ref="dropdownRef" 
                    class="sp-dropdown" 
                    role="dialog" 
                    aria-label="Search filters"
                    :style="dropdownStyle">
                    
                    <div class="sp-header">
                        <div class="sp-header-left">
                            <div class="sp-header-icon">
                                <i class="fas fa-sliders-h"></i>
                            </div>
                            <div>
                                <span class="sp-label">Search filters</span>
                                <span class="sp-sublabel" v-if="activeFilters > 0">
                                    {{ activeFilters }} filtro{{ activeFilters > 1 ? 's' : '' }} activo{{ activeFilters > 1 ? 's' : '' }}
                                </span>
                            </div>
                        </div>
                        <button type="button" class="sp-close" @click="close">
                            <i class="fas fa-times"></i>
                        </button>
                    </div>

                    <div class="sp-fields">
                        <slot name="form-content" />
                    </div>

                    <div class="sp-actions">
                        <div class="sp-actions-extra">
                            <slot name="actions-extra" />
                        </div>
                        <div class="sp-actions-right">
                            <button type="button" class="btn btn-danger btn-sm" @click="onClearClick">
                                <i class="fas fa-eraser"></i> Clear
                            </button>
                            <button type="button" class="btn btn-primary btn-sm" @click="onSearchClick">
                                <i class="fas fa-search"></i> Search
                            </button>
                        </div>
                    </div>
                </div>
            </Transition>
        </Teleport>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'

const props = defineProps<{
    title?: string
    initiallyOpen?: boolean
}>()

const emit = defineEmits<{
    (e: 'actionSearch'): void
    (e: 'actionClear'): void
}>()

const onSearchClick = () => {
    emit('actionSearch')
    close()
}

const onClearClick = () => {
    emit('actionClear')
}

const isOpen = ref(props.initiallyOpen ?? false)

const wrapperRef = ref<HTMLElement | null>(null)
const dropdownRef = ref<HTMLElement | null>(null)
const dropdownStyle = ref<Record<string, string>>({})

const activeFilters = computed(() => 0)

const toggle = () => {
    isOpen.value = !isOpen.value
    if (isOpen.value) nextTick(updateDropdownPosition)
}

const close = () => { isOpen.value = false }

const updateDropdownPosition = () => {
    if (!wrapperRef.value || !dropdownRef.value) return

    const rect = wrapperRef.value.getBoundingClientRect()
    const dropdownWidth = dropdownRef.value.offsetWidth
    const viewportWidth = window.innerWidth
    const margin = 10

    let left = rect.left + window.scrollX

    if (rect.left + dropdownWidth > viewportWidth - margin) {
        left = rect.right + window.scrollX - dropdownWidth
    }

    if (left < margin) left = margin

    const maxWidth = viewportWidth - margin * 2

    dropdownStyle.value = {
        top: `${rect.bottom + window.scrollY + 6}px`,
        left: `${left}px`,
        maxWidth: `${maxWidth}px`,
    }
}

const handleClickOutside = (e: MouseEvent) => {
    if (!isOpen.value) return
    const target = e.target as Node
    if (!wrapperRef.value?.contains(target) && !dropdownRef.value?.contains(target)) {
        close()
    }
}

const handleKeydown = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && isOpen.value) close()
}

onMounted(() => {
    document.addEventListener('click', handleClickOutside)
    document.addEventListener('keydown', handleKeydown)
    window.addEventListener('scroll', updateDropdownPosition, true)
    window.addEventListener('resize', updateDropdownPosition)
})

onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside)
    document.removeEventListener('keydown', handleKeydown)
    window.removeEventListener('scroll', updateDropdownPosition, true)
    window.removeEventListener('resize', updateDropdownPosition)
})
</script>

<style scoped>
.sp-wrapper .btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    height: 31px;
}

.sp-wrapper .btn i {
    font-size: 12px;
}

.sp-trigger {
    background: #f8f9fa;
    border: 1px solid #dee2e6;
    color: #495057;
    cursor: pointer;
    border-radius: 0;
}

.sp-trigger:hover {
    background: #e9ecef;
    border-color: #ced4da;
}

.sp-trigger.active {
    background: #e7f1ff;
    border-color: #86b7fe;
    color: #0d6efd;
}
</style>